import * as amplitude from "@amplitude/analytics-browser";
import * as Sentry from "@sentry/astro";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import type { InstagramData } from "../lib/types";
import Background from "./background";
import DownloadLocket from "./download-locket";
import Home from "./home";
import Loading from "./loading";
import Results from "./results";
import Tutorial from "./tutorial";
import Upload from "./upload";

if (import.meta.env.PROD) {
  amplitude.init("030e8f0ab8718638b4063ffb69ae4e68", {
    defaultTracking: true,
  });

  Sentry.init({
    dsn: "https://913a34760120e028efb99875b9b4c7ab@o1126632.ingest.us.sentry.io/4508785966841856",
  });
}

type Step =
  | "home"
  | "tutorial"
  | "tutorial-start-at-end"
  | "upload"
  | "loading-before-first-valentine"
  | "result-one"
  | "download-locket"
  | "loading-before-second-valentine"
  | "result-two"
  | "loading-before-third-valentine"
  | "result-three";

const DEV_START_STEP: Step = "upload";

export default function App() {
  const [data, setData] = useState<InstagramData | null>(null);
  const [step, setStep] = useState<Step>(
    import.meta.env.DEV
      ? DEV_START_STEP
      : location.search === "?upload"
      ? "upload"
      : "home"
  );
  const [downloadLocketUserName, setDownloadLocketUserName] = useState<
    string | null
  >(null);

  useEffect(() => {
    let themeColor = "#ffe9f3";

    if (["tutorial", "tutorial-start-at-end", "upload"].includes(step)) {
      themeColor = "#fff6fa";
    }

    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", themeColor);
  }, [step]);

  useEffect(() => {
    amplitude.track("page_view");

    if (location.search === "?upload") {
      amplitude.track("reminder_email_link_clicked");
      history.pushState({}, "", location.pathname);
    }
  }, []);

  return (
    <div
      className="bg-brand-light-pink xs:aspect-[9/16] xs:h-dvh xs:mx-auto"
      data-container
    >
      <Background>
        <div className="h-full">
          <AnimatePresence>
            {step === "home" && (
              <motion.div
                key="home"
                exit={{ opacity: 0 }}
                className="absolute inset-0"
              >
                <Home
                  onNext={() => {
                    if (import.meta.env.DEV) {
                      setStep("tutorial-start-at-end");
                      return;
                    }

                    setStep("tutorial");

                    amplitude.track("tutorial_started");
                  }}
                />
              </motion.div>
            )}
            {(step === "tutorial" || step === "tutorial-start-at-end") && (
              <motion.div
                key="tutorial"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0"
              >
                <Tutorial
                  onNext={() => {
                    setStep("upload");
                    amplitude.track("upload_viewed");
                  }}
                  startAtEnd={step === "tutorial-start-at-end"}
                />
              </motion.div>
            )}
            {step === "upload" && (
              <motion.div
                key="upload"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0"
              >
                <Upload
                  onBack={() => setStep("tutorial-start-at-end")}
                  onDataReady={(data) => {
                    setData(data);
                    setStep("loading-before-first-valentine");
                    amplitude.track("upload_completed");
                  }}
                />
              </motion.div>
            )}
            {step === "loading-before-first-valentine" && (
              <motion.div
                key="loading-before-first-valentine"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0"
              >
                <Loading
                  index={0}
                  onNext={() => {
                    amplitude.track("valentine_one_viewed");
                    setStep("result-one");
                  }}
                />
              </motion.div>
            )}
            {step === "result-one" && (
              <motion.div
                key="result-one"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0"
              >
                <Results
                  index={0}
                  data={data!}
                  onNext={(userName) => {
                    setDownloadLocketUserName(userName);
                    // setStep("download-locket");
                    // Going straight to result-two skips the "download locket" wall.
                    setStep("result-two");
                    amplitude.track("see_next_valentine_clicked");
                  }}
                />
              </motion.div>
            )}
            {step === "download-locket" && (
              <motion.div
                key="download-locket"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0"
              >
                <DownloadLocket
                  userName={downloadLocketUserName!}
                  onNext={() => setStep("loading-before-second-valentine")}
                />
              </motion.div>
            )}
            {step === "loading-before-second-valentine" && (
              <motion.div
                key="loading-before-second-valentine"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0"
              >
                <Loading
                  index={1}
                  onNext={() => {
                    if (data!.conversations.length < 2) {
                      alert(
                        "You don’t have enough Instagram DM history for a second valentine!"
                      );
                      return;
                    }

                    setStep("result-two");
                    amplitude.track("valentine_two_viewed");
                  }}
                />
              </motion.div>
            )}
            {step === "result-two" && (
              <motion.div
                key="result-two"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0"
              >
                <Results
                  index={1}
                  data={data!}
                  onNext={() => setStep("loading-before-third-valentine")}
                  onPrev={() => setStep("result-one")}
                />
              </motion.div>
            )}
            {step === "loading-before-third-valentine" && (
              <motion.div
                key="loading-before-third-valentine"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0"
              >
                <Loading
                  index={2}
                  onNext={() => {
                    if (data!.conversations.length < 3) {
                      alert(
                        "You don’t have enough Instagram DM history for a third valentine!"
                      );
                      return;
                    }

                    setStep("result-three");
                    amplitude.track("valentine_three_viewed");
                  }}
                />
              </motion.div>
            )}
            {step === "result-three" && (
              <motion.div
                key="result-three"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0"
              >
                <Results
                  index={2}
                  data={data!}
                  onNext={() => {
                    amplitude.track("locket_clicked_from_last_result");

                    if (/iPhone|iPad/.test(navigator.userAgent)) {
                      window.open(
                        "https://apps.apple.com/us/app/locket-widget/id1600525061?ppid=2866279e-a784-42c7-b0ec-d19f2e57343d",
                        "_blank"
                      );
                    } else {
                      window.open("https://locket.camera", "_blank");
                    }
                  }}
                  onPrev={() => setStep("result-two")}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Background>
    </div>
  );
}
